"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { MapPin, Phone, Mail, Loader, Send, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
function InquiryForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="222957" className="flex flex-col items-center justify-center py-12 text-center space-y-6 animate-in fade-in zoom-in duration-500">
        <div className="size-16 rounded-full bg-ring/10 flex items-center justify-center mb-4">
          <Send className="size-8 text-ring" />
        </div>
        <h3 className="text-2xl font-default font-semibold text-foreground">Message Received</h3>
        <p className="text-muted-foreground max-w-md">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-4 rounded-full px-8 border-ring text-ring hover:bg-ring/5">
          Send Another Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="name" rules={{
        required: "Full Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80 font-default">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Jane Doe" className="bg-secondary/40 backdrop-blur-sm border-border/60 focus-visible:ring-ring/40 rounded-xl" {...field} />
              </FormControl>
              <FormDescription className="text-xs opacity-70">How should we address you?</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email Address is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80 font-default">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="jane@example.com" className="bg-secondary/40 backdrop-blur-sm border-border/60 focus-visible:ring-ring/40 rounded-xl" {...field} />
              </FormControl>
              <FormDescription className="text-xs opacity-70">We'll send your quote here.</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80 font-default">Phone Number <span className="text-muted-foreground text-xs font-normal">(Optional)</span></FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(772) 555-0123" className="bg-secondary/40 backdrop-blur-sm border-border/60 focus-visible:ring-ring/40 rounded-xl" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="organization" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80 font-default">Property Address <span className="text-muted-foreground text-xs font-normal">(Optional)</span></FormLabel>
              <FormControl>
                <Input placeholder="123 Beach Lane, Vero Beach" className="bg-secondary/40 backdrop-blur-sm border-border/60 focus-visible:ring-ring/40 rounded-xl" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="interest" rules={{
      required: "Please select a service type"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/80 font-default">Service Type</FormLabel>
            <FormControl>
              <select className="flex h-10 w-full rounded-xl border border-border/60 bg-secondary/40 backdrop-blur-sm px-3 py-2 text-base shadow-xs transition-[color,box-shadow] outline-none focus-visible:border-ring focus-visible:ring-ring/40 focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 md:text-sm appearance-none" {...field}>
                <option value="" disabled>Select a service</option>
                <option value="residential">Residential Cleaning</option>
                <option value="commercial">Commercial Cleaning</option>
                <option value="airbnb">Airbnb / Vacation Rental</option>
                <option value="other">Other Inquiry</option>
              </select>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Please provide details about your needs"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/80 font-default">Tell us more about your needs</FormLabel>
            <FormControl>
              <Textarea placeholder="e.g., 3 bedrooms, pet-friendly, preferred frequency, or specific areas of concern..." className="min-h-[120px] bg-secondary/40 backdrop-blur-sm border-border/60 focus-visible:ring-ring/40 rounded-xl resize-none" {...field} />
            </FormControl>
            <FormDescription className="text-xs opacity-70">The more detail you provide, the more accurate our estimate will be.</FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 rounded-xl bg-destructive/10 border border-destructive/20 text-destructive text-sm flex items-start gap-3">
          <div className="mt-0.5"></div>
          <p>{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-base font-default font-semibold bg-accent text-accent-foreground border-0 hover:opacity-90 transition-opacity shadow-lg shadow-accent/20 rounded-full">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Calculating Quote...
          </> : <>
            Request Quote
            <ArrowRight className="ml-2 size-4" />
          </>}
      </Button>
    </Form>;
}
export default function InquiryPortal() {
  return <section id="lets-get-started" className="relative bg-background py-20 lg:py-32 overflow-hidden">
      
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute -top-[20%] -left-[10%] w-[50%] h-[50%] rounded-full bg-ring/5 blur-[120px]" />
        <div className="absolute top-[40%] -right-[10%] w-[40%] h-[60%] rounded-full bg-accent/5 blur-[120px]" />
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-start">
          
          
          <div className="lg:col-span-5 flex flex-col gap-10 lg:pr-8">
            <div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold text-foreground mb-4 tracking-tight">
                Ready for a <span className="text-transparent bg-clip-text bg-gradient-to-r from-ring to-chart-3">Sparkling</span> Home?
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed font-default">
                Whether you need a deep clean for your Vero Beach home, a quick turnover for your Airbnb, or a professional touch for your office, we're here to help you shine.
              </p>
            </div>

            <div className="space-y-6">
              <div className="flex items-start gap-4 group">
                <div className="size-12 rounded-2xl bg-card border border-border/50 flex items-center justify-center shrink-0 shadow-sm group-hover:border-ring/30 group-hover:bg-ring/5 transition-colors">
                  <Mail className="size-5 text-ring" />
                </div>
                <div>
                  <h4 className="font-default font-semibold text-foreground text-lg">Email Us</h4>
                  <p className="text-muted-foreground mb-1 font-default">For general inquiries and booking support.</p>
                  <Link to="mailto:hello@coastalcleaningvb.com" className="text-ring font-medium hover:underline inline-flex items-center gap-1 font-default">
                    hello@coastalcleaningvb.com
                  </Link>
                </div>
              </div>

              <div className="flex items-start gap-4 group">
                <div className="size-12 rounded-2xl bg-card border border-border/50 flex items-center justify-center shrink-0 shadow-sm group-hover:border-ring/30 group-hover:bg-ring/5 transition-colors">
                  <Phone className="size-5 text-ring" />
                </div>
                <div>
                  <h4 className="font-default font-semibold text-foreground text-lg">Call Us</h4>
                  <p className="text-muted-foreground mb-1 font-default">Mon-Fri: 8am-5pm; Sat: 9am-2pm</p>
                  <Link to="tel:+17725550123" className="text-ring font-medium hover:underline inline-flex items-center gap-1 font-default">
                    (772) 555-0123
                  </Link>
                </div>
              </div>

              <div className="flex items-start gap-4 group">
                <div className="size-12 rounded-2xl bg-card border border-border/50 flex items-center justify-center shrink-0 shadow-sm group-hover:border-ring/30 group-hover:bg-ring/5 transition-colors">
                  <MapPin className="size-5 text-ring" />
                </div>
                <div>
                  <h4 className="font-default font-semibold text-foreground text-lg">Our Service Area</h4>
                  <p className="text-muted-foreground font-default">
                    456 Ocean Drive<br />
                    Vero Beach, FL 32963
                  </p>
                </div>
              </div>
            </div>

            
            <div className="mt-4 rounded-3xl overflow-hidden border border-border/50 h-64 relative group shadow-sm">
              <div className="absolute inset-0 bg-muted/50 animate-pulse" />
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1635001/img/office_location_map_educity.png" alt="Map showing Coastal Cleaning service coverage in Vero Beach" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-4 right-4 bg-card/90 backdrop-blur-md border border-border/50 p-3 rounded-xl shadow-lg flex items-center gap-3">
                <div className="size-8 rounded-full bg-ring/20 flex items-center justify-center shrink-0">
                  <div className="size-3 rounded-full bg-ring animate-ping absolute" />
                  <div className="size-3 rounded-full bg-ring relative" />
                </div>
                <p className="text-sm font-default font-medium text-foreground truncate">Coastal Cleaning HQ</p>
              </div>
            </div>
          </div>

          
          <div className="lg:col-span-7 relative">
            
            <div className="absolute -inset-1 bg-gradient-to-r from-ring/20 via-chart-3/20 to-ring/20 rounded-[2.5rem] blur-xl opacity-50" />
            
            <Card className="relative bg-card/80 backdrop-blur-xl border-border/30 shadow-2xl rounded-3xl overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-ring via-chart-3 to-ring" />
              
              <CardContent className="p-8 sm:p-10">
                <div className="mb-8">
                  <h3 className="text-2xl font-default font-semibold text-foreground mb-2">Request a Quote</h3>
                  <p className="text-muted-foreground font-default">Tell us about your space and we&apos;ll send over a personalized cleaning estimate within 24 hours.</p>
                </div>

                <FormProvider formKey="get_started_form_book_your_clean" formId="get_started_form_book_your_clean" sectionName="Inquiry Portal" defaultValues={{
                name: "",
                email: "",
                phone: "",
                organization: "",
                interest: "",
                message: ""
              }} submitText="Request Quote" successMessage="Thank you for reaching out! We've received your request and our team will get back to you with a custom estimate shortly." errorMessage="There was an issue submitting your request. Please check your connection and try again.">
                  <InquiryForm />
                </FormProvider>
              </CardContent>
            </Card>
          </div>

        </div>
      </div>
    </section>;
}